import * as React from 'react';

import type { QueueEntry } from '@rematter/frontend-lib-api-client/grpc/sidecar/v1/local_api_pb';
import { Button, ConfirmPopoverButton } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { LucideRefreshCw, LucideTrash2 } from 'lucide-react';

import { useTranslations } from '../../../i18n/useTranslations';

import { useSidecarClient } from '~/shared/providers/libp2p';

function formatFileSize(
  bytes: bigint,
  units: {
    bytes: string;
    kilobytes: string;
    megabytes: string;
  },
): string {
  const n = Number(bytes);
  if (n < 1024) return `${n} ${units.bytes}`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} ${units.kilobytes}`;
  return `${(n / (1024 * 1024)).toFixed(1)} ${units.megabytes}`;
}

export type SidecarTabUploadQueueProps = {
  sidecarId: string;
};

export const SidecarTabUploadQueue = ({
  sidecarId,
}: SidecarTabUploadQueueProps) => {
  const t = useTranslations();
  const client = useSidecarClient(sidecarId);
  const toast = useToast();

  const [entries, setEntries] = React.useState<QueueEntry[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);

  const fetchEntries = React.useCallback(async () => {
    if (!client) return;
    try {
      setIsLoading(true);
      console.debug('[SidecarTabUploadQueue] listQueueEntries:start', {
        sidecarId,
      });
      const res = await client.listQueueEntries({});
      console.debug('[SidecarTabUploadQueue] listQueueEntries:success', {
        sidecarId,
        count: res.entries.length,
      });
      setEntries(res.entries);
    } catch (err) {
      console.error('[SidecarTabUploadQueue] listQueueEntries:error', {
        sidecarId,
        error: err instanceof Error ? err.message : String(err),
      });
      toast.error(
        err instanceof Error
          ? err.message
          : t.hardwareView.sidecarDetailView.uploadQueue.errors
              .listEntriesFallback,
      );
    } finally {
      setIsLoading(false);
    }
  }, [client, sidecarId, t]); // NOTE: Do not add `toast` to the dependency array, or this callback will break.

  React.useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleDelete = React.useCallback(
    async (id: string) => {
      if (!client) return;
      try {
        console.debug('[SidecarTabUploadQueue] deleteQueueEntry:start', {
          sidecarId,
          id,
        });
        await client.deleteQueueEntry({ id });
        console.debug('[SidecarTabUploadQueue] deleteQueueEntry:success', {
          sidecarId,
          id,
        });
        setEntries((prev) => prev.filter((e) => e.id !== id));
      } catch (err) {
        console.error('[SidecarTabUploadQueue] deleteQueueEntry:error', {
          sidecarId,
          id,
          error: err instanceof Error ? err.message : String(err),
        });
        toast.error(
          err instanceof Error
            ? err.message
            : t.hardwareView.sidecarDetailView.uploadQueue.errors
                .deleteEntryFallback,
        );
        throw err;
      }
    },
    [client, sidecarId, t, toast],
  );

  if (!client) {
    return (
      <div className="flex items-center justify-center p-8 text-foreground/50">
        {t.hardwareView.sidecarDetailView.uploadQueue.offline}
      </div>
    );
  }

  const cols = t.hardwareView.sidecarDetailView.uploadQueue.columns;

  return (
    <div className="@SidecarTabUploadQueue flex flex-col gap-4 p-4">
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="default"
          icon={<LucideRefreshCw className="size-4" />}
          onPress={fetchEntries}
          isDisabled={isLoading}
        >
          {t.hardwareView.sidecarDetailView.uploadQueue.refresh}
        </Button>
      </div>

      {isLoading && entries.length === 0 ? (
        <div className="flex items-center justify-center py-8">
          <div className="size-6 animate-spin rounded-full border-2 border-foreground/50 border-t-transparent" />
        </div>
      ) : entries.length === 0 ? (
        <div className="flex items-center justify-center p-8 text-foreground/50">
          {t.hardwareView.sidecarDetailView.uploadQueue.empty}
        </div>
      ) : (
        <div className="overflow-auto rounded-lg border border-neutral/20">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-neutral/20 bg-neutral/5 text-left">
                <th className="px-3 py-2 font-medium">{cols.id}</th>
                <th className="px-3 py-2 font-medium">{cols.cameraId}</th>
                <th className="px-3 py-2 font-medium">{cols.storageKey}</th>
                <th className="px-3 py-2 font-medium">{cols.contentType}</th>
                <th className="px-3 py-2 font-medium">{cols.fileSize}</th>
                <th className="px-3 py-2 font-medium">{cols.enqueuedAt}</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr
                  key={entry.id}
                  className="border-b border-neutral/10 hover:bg-neutral/5"
                >
                  <td className="max-w-[120px] truncate px-3 py-2 font-mono text-xs">
                    {entry.id}
                  </td>
                  <td className="max-w-[120px] truncate px-3 py-2 font-mono text-xs">
                    {entry.cameraUniqueId}
                  </td>
                  <td className="max-w-[200px] truncate px-3 py-2 text-xs">
                    {entry.storageKey}
                  </td>
                  <td className="px-3 py-2 text-xs">{entry.contentType}</td>
                  <td className="px-3 py-2 text-xs">
                    {formatFileSize(
                      entry.fileSize,
                      t.hardwareView.sidecarDetailView.uploadQueue
                        .fileSizeUnits,
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">{entry.enqueuedAt}</td>
                  <td className="px-3 py-2">
                    <ConfirmPopoverButton
                      onConfirm={() => handleDelete(entry.id)}
                      message={
                        t.hardwareView.sidecarDetailView.uploadQueue
                          .deleteConfirmation
                      }
                      confirmLabel={t.hardwareView.common.confirmDelete}
                      cancelLabel={t.hardwareView.common.cancel}
                      placement="top"
                      buttonProps={{
                        variant: 'outline',
                        size: 'small',
                        color: 'failure',
                        icon: <LucideTrash2 className="size-3" />,
                      }}
                    >
                      {t.hardwareView.sidecarDetailView.uploadQueue.delete}
                    </ConfirmPopoverButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
